import { useState } from 'react';
import { useUpdateEffect } from 'react-use';

import {
  DEFAULT_QUIET_HOURS_END as DEFAULT_END,
  DEFAULT_QUIET_HOURS_START as DEFAULT_START,
  QUIET_HOURS_SMS_DISCLAIMER as SMS_DISCLAIMER,
} from 'modules/campaigns/lib/constants';
import {
  FormBodyText,
  RadioButton,
  RadioOption,
  StyledCaption,
} from 'modules/workflowNodeEditors/EditorBody.styled';
import { ExpandableCheckbox } from 'modules/workflowNodeEditors/editorComponents';
import { MessageMedium, QuietHours } from 'modules/workflows';
import { TimeIn24HourClock, TimePicker } from 'ui/datePicker';
import { QuietHours as QuietHoursIcon } from 'ui/icons';
import { colors } from 'ui/theme';
import { Link } from 'ui/links';
import { HelpToggletip } from 'ui/tooltip';
import noop from 'utils/noop';

import { selectIsQuietHoursAbortCampaignFeatureFlagOn } from 'core/userContext';
import { useSelector } from 'react-redux';
import { ErrorText } from '../../BaseSendEditor.styles';
import { formatTime } from '../../BaseSendEditor.utils';
import {
  SendSkipRadioLabel,
  SendSkipStyledCaption,
  StyledIndentRowWrapper,
  TimePickersWrapper,
} from './QuietHoursCheckbox.styles';

export interface QuietHoursCheckboxProps {
  messageMedium: MessageMedium | string;
  /** The quiet hours */
  quietHours: QuietHours | null;
  /** Is there an error */
  hasError?: boolean;
  /** Is the checkbox disabled */
  isDisabled?: boolean;
  /** Text to show if quiet hours is not valid */
  validationText?: string;
  /** Callback when quiet hours is updated */
  onChange: (quietHours: QuietHours | null) => void;
}

export const QUIET_HOURS_TEST_ID = 'quiet-hours-test-id';

const defaultQuietHours = { startTime: DEFAULT_START, endTime: DEFAULT_END };
const getInitialHours = (qh: QuietHours | null, type: keyof typeof defaultQuietHours) =>
  qh?.[type] ? formatTime(qh[type]) : defaultQuietHours[type];

export const QuietHoursCheckbox = ({
  messageMedium,
  hasError = false,
  validationText = '',
  quietHours,
  onChange = noop,
  isDisabled = false,
}: QuietHoursCheckboxProps) => {
  const disclaimer = messageMedium === MessageMedium.SMS ? SMS_DISCLAIMER : '';
  const [isChecked, setIsChecked] = useState(!!quietHours);
  const [startTime, setStartTime] = useState(getInitialHours(quietHours, 'startTime'));
  const [endTime, setEndTime] = useState(getInitialHours(quietHours, 'endTime'));
  const [sendSkip, setSendSkip] = useState(quietHours?.sendSkip || false);
  const quietHoursAbortCampaignIsEnabled = useSelector(
    selectIsQuietHoursAbortCampaignFeatureFlagOn,
  );

  const updateStartTime = (time: TimeIn24HourClock) => {
    setStartTime(time);
    onChange({ endTime, startTime: time, sendSkip });
  };

  const updateEndTime = (time: TimeIn24HourClock) => {
    setEndTime(time);
    onChange({ startTime, endTime: time, sendSkip });
  };

  const updateSkip = (value: boolean) => {
    setSendSkip(value);
    onChange({ startTime, endTime, sendSkip: value });
  };

  useUpdateEffect(() => {
    onChange(isChecked ? { endTime, startTime, sendSkip } : null);
  }, [isChecked]);

  return (
    <ExpandableCheckbox
      caption={
        <>
          Prevents messages from being sent during part of the day. {disclaimer}
          &nbsp;
          <Link
            hasOpenNewTabIcon
            size="caption"
            href="https://support.iterable.com/hc/articles/7412316462996"
            rel="noreferrer noopener"
            target="_blank"
          >
            Learn more
          </Link>
        </>
      }
      id="quiet-hours"
      isChecked={isChecked}
      onEnabledUpdate={setIsChecked}
      label="Quiet hours"
      labelSupplement={<QuietHoursIcon width={16} height={16} />}
      data-test={QUIET_HOURS_TEST_ID}
      isDisabled={isDisabled}
    >
      <StyledIndentRowWrapper>
        <TimePickersWrapper>
          <FormBodyText>Prevent sends between</FormBodyText>
          <TimePicker
            time={startTime}
            inputClockFormat="24-hour"
            id="quiet-hours-start"
            increment={30}
            isError={hasError}
            onSelect={([time]) => updateStartTime(time as TimeIn24HourClock)}
            isDisabled={isDisabled}
          />
          <FormBodyText>and</FormBodyText>
          <TimePicker
            time={endTime}
            inputClockFormat="24-hour"
            id="quiet-hours-end"
            increment={30}
            isError={hasError}
            onSelect={([time]) => updateEndTime(time as TimeIn24HourClock)}
            isDisabled={isDisabled}
          />
          <HelpToggletip
            tooltipContent="User’s local time zone is used if available. Project time zone is used as a fallback."
            iconProps={{
              color: colors.secondaryText,
            }}
          />
        </TimePickersWrapper>
      </StyledIndentRowWrapper>
      {quietHoursAbortCampaignIsEnabled && (
        <>
          <SendSkipStyledCaption>
            If a message is triggered during quiet hours:
          </SendSkipStyledCaption>
          <StyledCaption>
            <RadioOption>
              <RadioButton
                id={'send-after-quiet-hours-end'}
                value={!sendSkip}
                isChecked={!sendSkip}
                onClick={() => updateSkip(false)}
                isDisabled={isDisabled}
              />
              <SendSkipRadioLabel>Send the message after quiet hours ends</SendSkipRadioLabel>
            </RadioOption>
            <RadioOption>
              <RadioButton
                id={'abort-campaign'}
                value={sendSkip}
                isChecked={sendSkip}
                onClick={() => updateSkip(true)}
                isDisabled={isDisabled}
              />
              <SendSkipRadioLabel>Cancel the send</SendSkipRadioLabel>
            </RadioOption>
          </StyledCaption>
        </>
      )}
      {hasError && <ErrorText>{validationText}</ErrorText>}
    </ExpandableCheckbox>
  );
};
